import type VueI18n from "vue-i18n";
import { Trait } from "./consts";

// ---------------------------------------------------------------------------
// Content Library
// All text content ported from:
//   content-resources/Logic Model - Participant Reports - v3 (Content Library - Competencies).csv
// ---------------------------------------------------------------------------

interface ContentLibraryEntry {
  summary: string;
  /** Alternate form of `summary` used only in the less-prioritized slot of the orientation
   *  summary sentence, via the `{lowLess}` placeholder. Populated by `resolveContentEntry`
   *  from the `Summary::Less` literal; falls back to `summary` when that literal is absent,
   *  so the hardcoded library below does not define it. */
  summaryLess?: string;
  description: string;
  behaviours: string[];
  pro1: string;
  pro2: string;
  pro3: string;
  con1: string;
  con2: string;
  con3: string;
  devSuggestions: string[];
}

const CONTENT_LIBRARY: Record<Trait, ContentLibraryEntry> = {
  goal: {
    summary: "keep organizational goals front and center",
    description: "Focuses on driving results in line with corporate objectives.",
    behaviours: ["Prioritizes cost-effective decisions.", "Sets and pursues milestones aligned to goals.", "Holds self and others to high standards.", "Considers organizational impact in decisions."],
    pro1: "Your goal-orientation may keep projects on track and targets met",
    pro2: "your goal-orientation may keep projects on track and targets met",
    pro3: "In addition, your goal-orientation may keep projects on track and targets met",
    con1: "A lower focus on organizational goals may impact alignment between day-to-day decisions and broader company objectives.",
    con2: "In addition, a lower focus on organizational goals may impact alignment between day-to-day decisions and broader company objectives.",
    con3: "A lower focus on organizational goals may also impact alignment between day-to-day decisions and broader company objectives.",
    devSuggestions: [
      "Sponsor enterprise-wide metrics dashboards that cascade organizational goals into business units, ensuring alignment and accountability at scale.",
      "Partner with a board mentor or enterprise strategist to refine how you communicate strategic alignment in business cases and executive forums.",
      'Read "Measure What Matters" by John Doerr or "Execution: The Discipline of Getting Things Done" by Larry Bossidy, Ram Charan, and Charles Burck. Study frameworks like FranklinCovey\'s The 4 Disciplines of Execution.',
    ],
  },
  decisive: {
    summary: "act quickly to address challenges and opportunities",
    description: "Takes action promptly and without hesitation.",
    behaviours: ["Prioritizes fast results.", "Removes blockers.", "Responds decisively with the best information at hand.", "Acts on the best available information."],
    pro1: "Your quick decision-making may minimize delays and bottlenecks",
    pro2: "your quick decision-making may minimize delays and bottlenecks",
    pro3: "In addition, your quick decision-making may minimize delays and bottlenecks",
    con1: "Being slower to decide can limit agility in situations where timely action is critical to maintaining momentum or capturing opportunities.",
    con2: "In addition, being slower to decide can limit agility in situations where timely action is critical to maintaining momentum or capturing opportunities.",
    con3: "Being slower to decide can also limit agility in situations where timely action is critical to maintaining momentum or capturing opportunities.",
    devSuggestions: [
      "Establish and delegate structured decision protocols by type (routine, tactical, strategic), clarifying when and how to escalate decisions for approval.",
      "Work with an executive coach to strengthen decision confidence and improve your ability to balance speed with due diligence.",
      'Study decision frameworks like OODA Loop. Read "Thinking in Bets: Making Smarter Decisions When You Don\'t Have All the Facts" by Annie Duke.',
    ],
  },
  adaptability: {
    summary: "adapt to new information as it surfaces",
    description: "Adapts goals and strategies when new information or conditions emerge.",
    behaviours: ["Revises plans and reallocates resources.", "Adopts alternative approaches when needed."],
    pro1: "Your flexibility may allow your team to respond quickly to changing conditions",
    pro2: "your flexibility may allow your team to respond quickly to changing conditions",
    pro3: "In addition, your flexibility may allow your team to respond quickly to changing conditions",
    con1: "Lower adaptability may make it harder to change course when priorities or circumstances change.",
    con2: "In addition, lower adaptability may make it harder to change course when priorities or circumstances change.",
    con3: "Lower adaptability may also make it harder to change course when priorities or circumstances change.",
    devSuggestions: [
      "Include change updates as a regular agenda item in leadership meetings, emphasizing how priorities are evolving and what’s being learned.",
      "Work with a transformation coach or change leadership mentor to refine how you manage ambiguity and stakeholder resistance.",
      "Read books like Leadership Agility: Five Levels of Mastery for Anticipating and Initiating Change by William Joiner and Stephen Josephs.",
    ],
  },
  con: {
    summary: "set and follow standard processes",
    description: "Instills and adheres to consistent methodologies and structure.",
    behaviours: ["Ensures projects follow plans.", "Explains decision criteria.", "Holds everyone accountable to workflows."],
    pro1: "Your process focus may keep work consistent and reliable",
    pro2: "your process focus may keep work consistent and reliable",
    pro3: "In addition, your process focus may keep work consistent and reliable",
    con1: "Adapting or bypassing processes can make it harder to ensure consistency and accountability.",
    con2: "In addition, adapting or bypassing processes can make it harder to ensure consistency and accountability.",
    con3: "Adapting or bypassing processes can also make it harder to ensure consistency and accountability.",
    devSuggestions: [
      "Require that all major projects follow a defined methodology with documented checkpoints, while allowing business units to adapt procedures for local execution.",
      "Invite feedback from your operations or audit leaders on how effectively you uphold and model adherence to enterprise processes.",
      'Read "Business Process Management" by John Jeston and Johan Nelis to learn frameworks for designing, managing, and sustaining process excellence across an organization.',
    ],
  },
  grit: {
    summary: "maintain composure and continue pushing through challenges",
    description: "Maintains composure through challenges, recovers quickly, and is tenacious and attached to one's own approach.",
    behaviours: ["Maintains composure under stress.", "Recovers after setbacks.", "Sticks to one's own approach rather than reacting to noise."],
    pro1: "Your tenacity may help ensure challenges are overcome and objectives are met",
    pro2: "your tenacity may help overcome challenges",
    pro3: "In addition, your tenacity may help overcome challenges",
    con1: "Lower emphasis on unyielding persistence may make it more difficult to maintain momentum through challenges, potentially leading to incomplete execution of plans.",
    con2: "In addition, a lower emphasis on unyielding persistence can make it more difficult to maintain momentum through challenges, potentially leading to incomplete execution of plans.",
    con3: "A lower emphasis on unyielding persistence can also make it more difficult to maintain momentum through challenges, potentially leading to incomplete execution of plans.",
    devSuggestions: [
      "Include change updates as a regular agenda item in leadership meetings, emphasizing how priorities are evolving and what's being learned.",
      "Commit yourself to reviewing major setbacks within 48 hours, identify lessons learned, and communicate next steps to the executive team.",
      'Read "The Hard Thing About Hard Things" by Ben Horowitz.',
    ],
  },
  tact: {
    summary: "take a diplomatic approach in sensitive situations",
    description: "Navigates interpersonal dynamics and business situations with sensitivity and strategy.",
    behaviours: ["Seeks mutually beneficial solutions.", "Chooses the right moment to engage.", "Frames difficult messages thoughtfully.", "Considers others' perspectives."],
    pro1: "Your tactfulness may preserve relationships and foster collaboration",
    pro2: "your tactfulness may preserve relationships and foster collaboration",
    pro3: "In addition, your tactfulness may preserve relationships and foster collaboration",
    con1: "Being less focused on tact and diplomacy may result in sensitive conversations not landing as intended, increasing the likelihood of friction in situations that require careful handling.",
    con2: "In addition, being less focused on tact and diplomacy may result in sensitive conversations not landing as intended, increasing the likelihood of friction in situations that require careful handling.",
    con3: "Being less focused on tact and diplomacy may also result in sensitive conversations not landing as intended, increasing the likelihood of friction in situations that require careful handling.",
    devSuggestions: [
      "When tensions rise, anchor the discussion in enterprise priorities or shared values rather than personal opinions.",
      "Practice with a mentor or executive coach using role-play scenarios to strengthen diplomatic communication under stress.",
      'Read "Conflict without Casualties" by Nate Regier.',
    ],
  },
  persuade: {
    summary: "spend time getting others aligned on decisions and initiatives",
    description: "Articulates a point of view and makes the effort to convince others.",
    behaviours: ["Tailors messages to the audience.", "Engages allies to shape a strategy.", "Frames ideas in ways that inspire action."],
    pro1: "Your emphasis on rallying support may help secure buy-in for initiatives",
    pro2: "your emphasis on rallying support may help secure buy-in for initiatives",
    pro3: "In addition, your emphasis on rallying support may help secure buy-in for initiatives",
    con1: "Being less focused on persuading others may result in ideas or decisions not gaining the intended buy-in, potentially making alignment and follow-through more difficult.",
    con2: "In addition, being less focused on persuading others may result in ideas or decisions not gaining the intended buy-in, potentially making alignment and follow-through more difficult.",
    con3: "Being less focused on persuading others may also result in ideas or decisions not gaining the intended buy-in, potentially making alignment and follow-through more difficult.",
    devSuggestions: [
      "Anticipate objections from decision-makers and proactively address them with data, storytelling, and credible evidence.",
      "Seek peer feedback on your persuasion style; refine how you use data and emotion to gain buy-in.",
      'Read "Influence: The Psychology of Persuasion" by Robert Cialdini or "The Story Factor: Inspiration, Influence, and Persuasion through the Art of Storytelling" by Annette Simmons.',
    ],
  },
  strategic: {
    summary: "anticipate future needs and plan ahead",
    description: "Plans overarching approaches and tactics to achieve long-term goals or vision.",
    behaviours: [
      "Anticipates future needs and plans deliberately.",
      "Leads planning conversations to ensure continuity and readiness.",
      "Invests in forward-looking solutions.",
      "Links short-term actions to long-term success.",
    ],
    pro1: "Your focus on strategy may keep your team prepared and aligned for what's ahead",
    pro2: "your focus on strategy may keep your team prepared and aligned for what's ahead",
    pro3: "In addition, your focus on strategy may keep your team prepared and aligned for what's ahead",
    con1: "Focusing less on strategic planning can make it harder to anticipate risks or position the team for future success.",
    con2: "In addition, focusing less on strategic planning can make it harder to anticipate risks or position the team for future success.",
    con3: "Focusing less on strategic planning can also make it harder to anticipate risks or position the team for future success.",
    devSuggestions: [
      "Allocate executive time quarterly to horizon scanning and scenario planning to anticipate emerging risks and opportunities. Ensure findings are integrated in strategy reviews.",
      "Partner with a futurist, board member, or industry advisor to challenge and expand your long-term thinking.",
      'Study McKinsey\'s "Three Horizons Framework" for long-term growth or Salesforce\'s V2MOM process for how to translate vision into tactical plans. Learn strategic forecasting tools and techniques (e.g., "Strategic Foresight: Learning from the Future" by Patricia Lustig).',
    ],
  },
  intellect: {
    summary: "consider and encourage new and creative ideas",
    description: "Actively searches for new and creative ideas and solutions.",
    behaviours: ["Makes innovation a core factor in decisions.", "Creates conditions that support new ideas."],
    pro1: "Your innovation may drive creative solutions and keep the organization competitive",
    pro2: "your innovation may drive creative solutions and keep the organization competitive",
    pro3: "In addition, your innovation may drive creative solutions and keep the organization competitive",
    con1: "Deprioritizing innovation can make it harder to challenge outdated practices or stay competitive in evolving markets.",
    con2: "In addition, deprioritizing innovation can make it harder to challenge outdated practices or stay competitive in evolving markets.",
    con3: "Deprioritizing innovation can also make it harder to challenge outdated practices or stay competitive in evolving markets.",
    devSuggestions: [
      "Identify a project or initiative that has been stalling. Form at least one cross-functional innovation team that leverages diverse expertise to generate and prototype new ideas. Have an executive committee review outcomes.",
      "Seek mentorship from innovation leaders (internal or external) to learn governance and risk-balancing approaches.",
      'Read "The Innovator\'s Dilemma" by Clayton Christensen.',
    ],
  },
  vision: {
    summary: "craft and instill a vision for others",
    description: "Creates and communicates a clear direction for others to follow.",
    behaviours: ["Paints a clear picture of where the team is heading and why it matters.", "Connects tasks to the bigger picture.", "Frames changes in terms of long-term goals."],
    pro1: "Your vision-setting may rally your team around a common goal",
    pro2: "your vision-setting may rally your team around a common goal",
    pro3: "In addition, your vision-setting may rally your team around a common goal",
    con1: "Being less focused on establishing a vision can make it harder for others to connect their work to a broader purpose and stay aligned.",
    con2: "In addition, being less focused on establishing a vision can make it harder for others to connect their work to a broader purpose and stay aligned.",
    con3: "Being less focused on establishing a vision can also make it harder for others to connect their work to a broader purpose and stay aligned.",
    devSuggestions: [
      "Define and articulate a unifying vision that connects long-term goals to short-term priorities. Ensure leaders across functions can clearly describe how their work contributes to the broader vision.",
      "Work with a communications coach to enhance your storytelling and inspirational leadership style.",
      'Read "Start with Why" by Simon Sinek, "The Vision Driven Leader" by Michael S. Hyatt, or "Leading With Vision: The Leader\'s Blueprint for Creating a Compelling Vision and Engaging the Workforce" by Bonnie Hagemann, Simon Vetter, and John Maketa.',
    ],
  },
  empathy: {
    summary: "put others' feelings and well-being at the forefront of your decisions",
    description: "Considers people's feelings and well-being when leading and making decisions.",
    behaviours: ["Weighs how decisions will make people feel.", "Treats employee well-being as equally important as business results.", "Follows up to ensure others feel heard."],
    pro1: "Your empathy may create a supportive team culture",
    pro2: "your empathy may create a supportive team culture",
    pro3: "In addition, your empathy may create a supportive team culture",
    con1: "Deprioritizing how decisions affect others can sometimes lead to a team environment that feels transactional or disconnected.",
    con2: "In addition, deprioritizing how decisions affect others can sometimes lead to a team environment that feels transactional or disconnected.",
    con3: "Deprioritizing how decisions affect others can also sometimes lead to a team environment that feels transactional or disconnected.",
    devSuggestions: [
      "Reinforce a culture where care, respect, and psychological safety are non-negotiable in all interactions by setting clear behavioral expectations for all leaders and addressing breaches directly and consistently. Ensure leaders are recognized for modeling these behaviors and coached when they fall short.",
      "Work with an executive coach specializing in emotional intelligence (EQ) to deepen self-awareness and empathy.",
      'Read "Emotional Intelligence 2.0" by Travis Bradberry or "Applied Empathy: The New Language of Leadership" by Michael Ventura to deepen your understanding of empathy and emotional intelligence in leadership.',
    ],
  },
  mentor: {
    summary: "keep others' growth and development front of mind",
    description: "Develops others' professional capabilities and supports their personal growth.",
    behaviours: [
      "Gives honest, constructive feedback.",
      "Provides stretch opportunities.",
      "Advises with long-term development in mind.",
      "Encourages opportunities that support growth, even if not immediately beneficial to the team.",
    ],
    pro1: "Your mentorship may help develop future leaders",
    pro2: "your mentorship may help develop future leaders",
    pro3: "In addition, your mentorship may help develop future leaders",
    con1: "Being less focused on mentoring others may result in less emphasis on building capability within the team, which can affect how effectively work is sustained or scaled over time.",
    con2: "In addition, being less focused on mentoring others may result in less emphasis on building capability within the team, which can affect how effectively work is sustained or scaled over time.",
    con3: "Being less focused on mentoring others may also result in less emphasis on building capability within the team, which can affect how effectively work is sustained or scaled over time.",
    devSuggestions: [
      "Meet with your direct reports quarterly to discuss development goals, progress, and challenges.",
      "Receive mentor training or peer coaching on feedback delivery and developmental goal setting.",
      'Read "Multipliers" by Liz Wiseman or "Leader\'s Guide to Coaching and Mentoring" by Mike Brent and Fiona Elsa Dent.',
    ],
  },
  relationship: {
    summary: "invest in building and maintaining key relationships over time",
    description: "Nurtures and leverages connections with individuals, groups, and key stakeholders.",
    behaviours: ["Maintains regular communication.", "Leverages networks to secure resources.", "Facilitates dialogue to resolve conflict."],
    pro1: "Your focus on relationship maintenance may ensure ongoing support",
    pro2: "your focus on relationship maintenance may ensure ongoing support",
    pro3: "In addition, your focus on relationship maintenance may ensure ongoing support",
    con1: "Focusing less on stakeholder relationships may limit support for key initiatives.",
    con2: "In addition, focusing less on stakeholder relationships may limit support for key initiatives.",
    con3: "Focusing less on stakeholder relationships may also limit support for key initiatives.",
    devSuggestions: [
      "Personally engage with the company's top 10 strategic stakeholders through quarterly check-ins or joint initiatives.",
      "Shadow a colleague with strong stakeholder relationships to observe their approach.",
      'Read "Give and Take: Why Helping Others Drives Our Success" by Adam Grant or "Trusted Advisor" by David Maister, Robert Galford, and Charles Green.',
    ],
  },
  integrity: {
    summary: "anchor your decisions in social values",
    description: "Commits to fostering positive social impact above profit maximization.",
    behaviours: ["Shares the truth about decisions openly.", "Prioritizes social good when trade-offs arise.", "Speaks up against harmful behaviors toward employees or communities."],
    pro1: "The standards you uphold may strengthen integrity and protect stakeholders",
    pro2: "the standards you uphold may strengthen integrity and protect stakeholders",
    pro3: "In addition, the standards you uphold may strengthen and protect stakeholders",
    con1: "Being less focused on social values may result in decisions being shaped more by immediate business priorities, with less consideration of their broader or longer-term impact on stakeholders.",
    con2: "In addition, being less focused on social values may result in decisions being shaped more by immediate business priorities, with less consideration of their broader or longer-term impact on stakeholders.",
    con3: "Being less focused on social values may also result in decisions being shaped more by immediate business priorities, with less consideration of their broader or longer-term impact on stakeholders.",
    devSuggestions: [
      "Embed ESG (environmental, social, governance) metrics into enterprise scorecards and report progress transparently.",
      "Partner with a sustainability or corporate social responsibility (CSR) leader to understand how to embed social values in operations.",
      'Read "Net Positive" by Paul Polman to explore responsible business transformation.',
    ],
  },
  auth: {
    summary: "communicate openly with others",
    description: "Communicates in a direct, open, and authentic manner.",
    behaviours: ["Explains reasons behind decisions.", "Discloses relevant information.", "Gives clear feedback."],
    pro1: "Your transparency may reduce uncertainty and keep others aligned",
    pro2: "your transparency may reduce uncertainty and keep others aligned",
    pro3: "In addition, your transparency may reduce uncertainty and keep others aligned",
    con1: "Less focus on communicating transparently may result in others having less visibility into the reasoning behind decisions, requiring them to interpret or fill in gaps.",
    con2: "In addition, less focus on communicating transparently may result in others having less visibility into the reasoning behind decisions, requiring them to interpret or fill in gaps.",
    con3: "Less focus on communicating transparently may also result in others having less visibility into the reasoning behind decisions, requiring them to interpret or fill in gaps.",
    devSuggestions: [
      "On a quarterly basis, model transparency by openly briefing all levels of the organization on both successes and challenges, including implications for strategy.",
      "Work with a communications coach to improve authenticity, message framing, and emotional tone.",
      'Read "Radical Candor" by Kim Scott or "Transparency: How Leaders Create a Culture of Candor" by Warren Bennis, Daniel Goleman, and Pat Ward Biederman to learn how to model open communication.',
    ],
  },
};

// ---------------------------------------------------------------------------
// Category Text
// Ported from the `category_text` list in the R script.
// Keys use the Vue orientation IDs (matching FOUR_GROUP_TRAIT_GROUPINGS).
// ---------------------------------------------------------------------------

/** The five patterns sentence 2 can take, selected by which score buckets are occupied.
 *  Placeholders are substituted by `interpolateTemplate`:
 *    {high} {mid} {low}  — joined trait `Summary` phrases for that bucket
 *    {lowLess}           — joined trait `Summary::Less` phrases for the low bucket
 *    {lessMix} {lessZeroOne} — the LessPhrase literals
 *  These defaults are identical for all four orientations, so they live here rather than
 *  being repeated inside each CATEGORY_TEXT entry. */
const DEFAULT_SENTENCE2_TEMPLATES = {
  midOnly: "You tend to {mid}.",
  midLow: "You tend to {mid}, but are {lessMix} {low}.",
  highMid: "When making decisions, you often {high}. You also {mid}.",
  highLow: "You show a strong tendency to {high}, but are {lessZeroOne} {low}.",
  highMidLow: "You show a strong tendency to {high}. You also {mid}. However, you are {lessMix} {low}.",
};

type Sentence2Templates = typeof DEFAULT_SENTENCE2_TEMPLATES;

interface CategoryTextEntry {
  sentence1_all_0: string;
  sentence1_all_1: string;
  sentence1_all_2: string;
  sentence3_all_nonzero: string;
  sentence3_all_0: string;
  sentence4_all_nonzero: string;
  sentence4_all_0: string;
  less_phrase_mix: string;
  less_phrase_zero_one: string;
  /** Populated by `resolveCategoryText` from the `Sentence2::*` literals; falls back to
   *  DEFAULT_SENTENCE2_TEMPLATES, so the hardcoded table below does not define it. */
  sentence2?: Sentence2Templates;
}

const CATEGORY_TEXT: Record<string, CategoryTextEntry> = {
  "communication-orientation": {
    sentence1_all_0:
      "Competencies in this category do not feature strongly in how you make decisions. You may focus more on your own work and task delivery than on shaping messages, seeking buy-in, or navigating social dynamics. ",
    sentence1_all_1: "Each competency in this category plays a role in your approach to decisions, even if none stand out as highly prioritized. ",
    sentence1_all_2:
      "You focus strongly on how you communicate with others. When making decisions, you tend to be open and direct while choosing words carefully to preserve relationships. You also work to secure buy-in and get others on board. ",
    sentence3_all_nonzero: "Your focus on communication and influence may build clarity and alignment, including when managing relationships or delicate situations. ",
    sentence3_all_0: "Focusing less on communication nuances may help you avoid overthinking and make decisions without getting bogged down in discussions. ",
    sentence4_all_nonzero: "Being highly attentive to communication nuances and influence can sometimes slow down messaging when quick responses are needed. ",
    sentence4_all_0: "Limited communication can lead to confusion, misaligned expectations, and missed opportunities to get stakeholders on board. ",
    less_phrase_mix: "less likely to try to",
    less_phrase_zero_one: "less prone to",
  },
  "strategic-orientation": {
    sentence1_all_0:
      "Competencies in this category do not feature strongly in how you make decisions. You tend to focus more on what's happening now than on big-picture, future-forward planning. You may stick with proven methods and avoid big changes or new ideas unless necessary. ",
    sentence1_all_1: "You draw on each competency in this category when making decisions, though none stand out as highly prioritized. ",
    sentence1_all_2: "You tend to think ahead, plan long-term, look for new ideas, and adjust quickly when things change. You communicate the big picture and guide others toward what's next. ",
    sentence3_all_nonzero: "By looking ahead, you may help the team stay focused and aligned on a direction.",
    sentence3_all_0: "Being focused on the present moment may help you avoid distractions and keep things running smoothly in the short term. ",
    sentence4_all_nonzero: "Focusing on the big picture can sometimes risk losing sight of short-term needs. ",
    sentence4_all_0: "Focusing less on the future can make it harder to adapt, plan ahead, or align others around a long-term direction. ",
    less_phrase_mix: "less likely to try to",
    less_phrase_zero_one: "less prone to",
  },
  "outcome-orientation": {
    sentence1_all_0:
      "Competencies in this category do not feature strongly in how you make decisions. You place less emphasis on quick action, process, or hitting hard targets, focusing on other considerations before strict performance, speed, or outcomes. ",
    sentence1_all_1: "You draw on each competency in this category when making decisions, though none stand out as highly prioritized. ",
    sentence1_all_2:
      "You bring a results-first mindset to decision-making. You take quick action, set clear goals, follow processes carefully, while pushing through challenges to get things done. You focus on impact and expect high standards from yourself and others. ",
    sentence3_all_nonzero: "Your tendency to focus on outcomes may help you keep things moving and bring structure and discipline to the team. ",
    sentence3_all_0: "Placing less emphasis on outcomes may free you to weigh the long-term impact and broader trade-offs before acting.",
    sentence4_all_nonzero: "Strongly focusing on results can sometimes risk pushing others too hard or being too rigid when flexibility is needed. ",
    sentence4_all_0: "Progress can slow down when speed, structure, or targets are not emphasized. ",
    less_phrase_mix: "less likely to try to",
    less_phrase_zero_one: "less prone to",
  },
  // Note: "poeple-orientation" is the key used in FOUR_GROUP_TRAIT_GROUPINGS (typo preserved)
  "poeple-orientation": {
    sentence1_all_0: "Competencies in this category do not feature strongly in how you make decisions. You may be more focused on delivering outcomes than on the impact of decisions on others and on the social good. ",
    sentence1_all_1: "You draw on each competency in this category when making decisions, though none stand out as highly prioritized. ",
    sentence1_all_2:
      "You tend to put people first when making decisions. You attend to others' feelings and well-being, provide feedback and growth opportunities, and prioritize social good even when it's uncomfortable or less profitable. ",
    sentence3_all_nonzero: "You tend to lead with care, prioritizing others' well-being, growth, and social values. ",
    sentence3_all_0: "This approach may help you stay focused on tasks and avoid being swayed by emotion or slowed down by the demands of mentorship.",
    sentence4_all_nonzero: "Putting people first can sometimes make it harder to make tough calls and be efficient in results-driven environments. ",
    sentence4_all_0:
      "Placing less attention on how others feel can make it harder for people to stay motivated. Paying less attention to social impact can also create reputational risk, especially in environments where social responsibility is expected. ",
    less_phrase_mix: "less inclined to",
    less_phrase_zero_one: "less apt to",
  },
};

// ---------------------------------------------------------------------------
// Orientation → trait list (mirrors FOUR_GROUP_TRAIT_GROUPINGS from consts.ts)
// ---------------------------------------------------------------------------

const ORIENTATION_TRAITS: Record<string, Trait[]> = {
  "strategic-orientation": ["strategic", "intellect", "vision"],
  "poeple-orientation": ["empathy", "mentor", "integrity"],
  "outcome-orientation": ["goal", "decisive", "con", "grit", "adaptability"],
  "communication-orientation": ["auth", "tact", "persuade", "relationship"],
};

// ---------------------------------------------------------------------------
// Literal resolution
// When an i18n instance is provided, each content fragment is looked up in the
// consumer's literal dictionary (e.g. report:elra v8 or
// project-report:evaluation-report) and falls back to the hardcoded content
// above when the key is absent or blank. Keys follow the scheme:
//   Report::DynamicText::Trait::{trait}::Summary|Summary::Less|Description|Pro::{n}|Con::{n}|Behaviour::{n}|DevSuggestion::{n}
//   Report::DynamicText::Orientation::{id}::Sentence1::All0|...|Sentence2::{Pattern}|LessPhrase::Mix|ZeroOne
// The full key→default-text mapping lives in literals/dynamic-text-literals.json.
// ---------------------------------------------------------------------------

const TRAIT_KEY_PREFIX = "Report::DynamicText::Trait";
const ORIENTATION_KEY_PREFIX = "Report::DynamicText::Orientation";

/** Report literals are always fetched with lang "en" (see GET_LITERALS), so
 *  lookups pin the locale explicitly rather than relying on the instance locale. */
const LITERAL_LOCALE = "en";

function resolveLiteral(i18n: VueI18n | undefined, key: string, fallback: string): string {
  if (!i18n || !i18n.te(key, LITERAL_LOCALE)) return fallback;
  const value = i18n.t(key, LITERAL_LOCALE).toString();
  return value.trim() === "" ? fallback : value;
}

/** Resolves a numeric-suffixed key series (::1, ::2, ...) into an array.
 *  A literal array replaces the hardcoded array wholesale; if ::1 is missing
 *  or every item is blank, the hardcoded array is used instead. */
function resolveLiteralArray(i18n: VueI18n | undefined, baseKey: string, fallback: string[]): string[] {
  if (!i18n || !i18n.te(`${baseKey}::1`, LITERAL_LOCALE)) return fallback;
  const items: string[] = [];
  for (let n = 1; i18n.te(`${baseKey}::${n}`, LITERAL_LOCALE); n++) {
    items.push(i18n.t(`${baseKey}::${n}`, LITERAL_LOCALE).toString());
  }
  const nonEmpty = items.filter((s) => s.trim() !== "");
  return nonEmpty.length > 0 ? nonEmpty : fallback;
}

function resolveContentEntry(trait: Trait, i18n?: VueI18n): ContentLibraryEntry {
  const entry = CONTENT_LIBRARY[trait];
  if (!entry || !i18n) return entry;
  const base = `${TRAIT_KEY_PREFIX}::${trait}`;
  const summary = resolveLiteral(i18n, `${base}::Summary`, entry.summary);
  return {
    summary,
    // Falls back to the *resolved* summary, so editing Summary alone keeps the two consistent
    summaryLess: resolveLiteral(i18n, `${base}::Summary::Less`, summary),
    description: resolveLiteral(i18n, `${base}::Description`, entry.description),
    behaviours: resolveLiteralArray(i18n, `${base}::Behaviour`, entry.behaviours),
    pro1: resolveLiteral(i18n, `${base}::Pro::1`, entry.pro1),
    pro2: resolveLiteral(i18n, `${base}::Pro::2`, entry.pro2),
    pro3: resolveLiteral(i18n, `${base}::Pro::3`, entry.pro3),
    con1: resolveLiteral(i18n, `${base}::Con::1`, entry.con1),
    con2: resolveLiteral(i18n, `${base}::Con::2`, entry.con2),
    con3: resolveLiteral(i18n, `${base}::Con::3`, entry.con3),
    devSuggestions: resolveLiteralArray(i18n, `${base}::DevSuggestion`, entry.devSuggestions),
  };
}

function resolveCategoryText(orientationId: string, i18n?: VueI18n): CategoryTextEntry | undefined {
  const entry = CATEGORY_TEXT[orientationId];
  if (!entry || !i18n) return entry;
  const base = `${ORIENTATION_KEY_PREFIX}::${orientationId}`;
  return {
    sentence1_all_0: resolveLiteral(i18n, `${base}::Sentence1::All0`, entry.sentence1_all_0),
    sentence1_all_1: resolveLiteral(i18n, `${base}::Sentence1::All1`, entry.sentence1_all_1),
    sentence1_all_2: resolveLiteral(i18n, `${base}::Sentence1::All2`, entry.sentence1_all_2),
    sentence3_all_nonzero: resolveLiteral(i18n, `${base}::Sentence3::AllNonzero`, entry.sentence3_all_nonzero),
    sentence3_all_0: resolveLiteral(i18n, `${base}::Sentence3::All0`, entry.sentence3_all_0),
    sentence4_all_nonzero: resolveLiteral(i18n, `${base}::Sentence4::AllNonzero`, entry.sentence4_all_nonzero),
    sentence4_all_0: resolveLiteral(i18n, `${base}::Sentence4::All0`, entry.sentence4_all_0),
    less_phrase_mix: resolveLiteral(i18n, `${base}::LessPhrase::Mix`, entry.less_phrase_mix),
    less_phrase_zero_one: resolveLiteral(i18n, `${base}::LessPhrase::ZeroOne`, entry.less_phrase_zero_one),
    sentence2: {
      midOnly: resolveLiteral(i18n, `${base}::Sentence2::MidOnly`, DEFAULT_SENTENCE2_TEMPLATES.midOnly),
      midLow: resolveLiteral(i18n, `${base}::Sentence2::MidLow`, DEFAULT_SENTENCE2_TEMPLATES.midLow),
      highMid: resolveLiteral(i18n, `${base}::Sentence2::HighMid`, DEFAULT_SENTENCE2_TEMPLATES.highMid),
      highLow: resolveLiteral(i18n, `${base}::Sentence2::HighLow`, DEFAULT_SENTENCE2_TEMPLATES.highLow),
      highMidLow: resolveLiteral(i18n, `${base}::Sentence2::HighMidLow`, DEFAULT_SENTENCE2_TEMPLATES.highMidLow),
    },
  };
}

// ---------------------------------------------------------------------------
// Helper functions (ports of R helper functions)
// ---------------------------------------------------------------------------

/** Joins description fragments: 1→bare, 2→"A and B", 3+→"A, B, and C" */
function joinSummaries(items: string[]): string {
  const clean = items.filter((s) => s.trim() !== "");
  if (clean.length === 0) return "";
  if (clean.length === 1) return clean[0];
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(", ")}, and ${clean[clean.length - 1]}`;
}

/** Joins pro text fragments into a grammatical sentence.
 *  Mirrors R's join_pros: strips trailing punct, removes "In addition," / "and " prefixes,
 *  capitalizes first, joins with "and"/";" and adds final period. */
function joinPros(items: string[]): string {
  let frags = items.map((s) => s.trim()).filter((s) => s !== "");
  if (frags.length === 0) return "";

  frags = frags.map((s) => s.replace(/[.,;:!?]+$/, ""));
  frags = frags.map((s) => s.replace(/^[Ii]n addition,\s*/i, ""));
  frags = frags.map((s) => s.replace(/^and\s+/i, ""));

  // Capitalize the leading fragment. Fragments are otherwise taken verbatim: the pronoun is
  // the literal author's to choose, so no article or possessive is injected here.
  frags[0] = frags[0].charAt(0).toUpperCase() + frags[0].slice(1);

  let joined: string;
  if (frags.length === 1) {
    joined = frags[0];
  } else if (frags.length === 2) {
    joined = `${frags[0]} and ${frags[1]}`;
  } else {
    joined = `${frags.slice(0, -1).join("; ")}; and ${frags[frags.length - 1]}`;
  }

  return `${joined}.`;
}

/** Joins con text fragments with spaces. */
function joinCons(items: string[]): string {
  const clean = items.map((s) => s.trim()).filter((s) => s !== "");
  if (clean.length === 0) return "";
  if (clean.length === 1) return clean[0];
  return clean.join(" ");
}

/** Returns the appropriate pro column value for an entry at a given position index (0-based).
 *  Position 0 → pro1, position 1 → pro2, position 2+ → pro3 */
function getProVariant(entry: ContentLibraryEntry, positionIndex: number): string {
  if (positionIndex === 0) return entry.pro1;
  if (positionIndex === 1) return entry.pro2;
  return entry.pro3;
}

/** Returns the appropriate con column value for an entry at a given position index (0-based). */
function getConVariant(entry: ContentLibraryEntry, positionIndex: number): string {
  if (positionIndex === 0) return entry.con1;
  if (positionIndex === 1) return entry.con2;
  return entry.con3;
}

/** Builds joined pro text for a list of traits using position-based column variants. */
function buildProText(traits: Trait[], lookup: (t: Trait) => ContentLibraryEntry): string {
  if (traits.length === 0) return "";
  const fragments = traits.map((trait, i) => getProVariant(lookup(trait), i));
  return joinPros(fragments);
}

/** Builds joined con text for a list of traits using position-based column variants. */
function buildConText(traits: Trait[], lookup: (t: Trait) => ContentLibraryEntry): string {
  if (traits.length === 0) return "";
  const fragments = traits.map((trait, i) => getConVariant(lookup(trait), i));
  return joinCons(fragments);
}

/** Builds joined summary text for a list of traits.
 *  `field` selects which phrase form to join: "summary" (the default, infinitive) or
 *  "summaryLess" (the alternate low-slot form, falling back to "summary" when unset). */
function buildSummaryText(traits: Trait[], lookup: (t: Trait) => ContentLibraryEntry, field: "summary" | "summaryLess" = "summary"): string {
  if (traits.length === 0) return "";
  const summaries = traits.map((t) => {
    const entry = lookup(t);
    return (field === "summaryLess" ? entry.summaryLess : undefined) ?? entry.summary;
  });
  return joinSummaries(summaries);
}

/** Substitutes {name} placeholders in a sentence template. Unknown names are left verbatim
 *  so a typo shows up in QA rather than silently blanking part of the sentence. */
function interpolateTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, name) => (name in vars ? vars[name] : match));
}

/** Strips a single trailing sentence-ending punctuation character. */
function stripTrailingPunct(s: string): string {
  return s.replace(/[.?!]$/, "");
}

function buildSentence1(count0: number, count1: number, count2: number, total: number, txt: CategoryTextEntry): string {
  if (count0 === total) return txt.sentence1_all_0;
  if (count1 === total) return txt.sentence1_all_1;
  if (count2 === total) return txt.sentence1_all_2;
  return "";
}

function buildSentence2(count0: number, count1: number, count2: number, highText: string, midText: string, lowText: string, lowLessText: string, txt: CategoryTextEntry): string {
  const templates = txt.sentence2 ?? DEFAULT_SENTENCE2_TEMPLATES;

  let template: string;
  if (count1 > 0 && count0 === 0 && count2 === 0) {
    // all prioritized (count1 == total already handled by sentence1, but this covers mixed counts)
    template = templates.midOnly;
  } else if (count1 > 0 && count0 > 0 && count2 === 0) {
    template = templates.midLow;
  } else if (count1 > 0 && count0 === 0 && count2 > 0) {
    template = templates.highMid;
  } else if (count1 === 0 && count0 > 0 && count2 > 0) {
    template = templates.highLow;
  } else if (count1 > 0 && count0 > 0 && count2 > 0) {
    template = templates.highMidLow;
  } else {
    return "";
  }

  const c = stripTrailingPunct;
  return interpolateTemplate(template, {
    high: c(highText),
    mid: c(midText),
    low: c(lowText),
    lowLess: c(lowLessText),
    lessMix: txt.less_phrase_mix,
    lessZeroOne: txt.less_phrase_zero_one,
  });
}

function buildSentence3(count0: number, count1: number, count2: number, proMid: string, proHigh: string, txt: CategoryTextEntry): string {
  if (count0 === 0) return txt.sentence3_all_nonzero;
  if (count2 === 0 && count1 === 0) return txt.sentence3_all_0;
  if (count2 === 0 && count1 > 0) return proMid;
  return proHigh;
}

function buildSentence4(count0: number, count1: number, count2: number, conLow: string, txt: CategoryTextEntry): string {
  if (count0 === 0) return txt.sentence4_all_nonzero;
  if (count2 === 0 && count1 === 0) return txt.sentence4_all_0;
  return conLow;
}

// ---------------------------------------------------------------------------
// Exported types and functions
// ---------------------------------------------------------------------------

export interface OrientationTextResult {
  summary: string;
  strengths: string;
  tradeoffs: string;
}

/**
 * Generates the dynamic orientation summary text (summary, strengths, trade-offs)
 * for a given orientation ID using the candidate's scoring data.
 *
 * @param orientationId - One of the FOUR_GROUP_TRAIT_GROUPINGS keys
 * @param scoring - The `data.data.scoring` object containing `{trait}_status` fields
 * @param i18n - Optional consumer i18n instance; when provided, literal keys override the hardcoded content per fragment
 */
export function getOrientationText(orientationId: string, scoring: Record<string, number>, i18n?: VueI18n): OrientationTextResult {
  const traits = ORIENTATION_TRAITS[orientationId] ?? [];
  const txt = resolveCategoryText(orientationId, i18n);

  if (!txt || traits.length === 0) {
    return { summary: "", strengths: "", tradeoffs: "" };
  }

  const resolvedEntries = new Map<Trait, ContentLibraryEntry>();
  const lookup = (t: Trait): ContentLibraryEntry => {
    let entry = resolvedEntries.get(t);
    if (!entry) {
      entry = resolveContentEntry(t, i18n);
      resolvedEntries.set(t, entry);
    }
    return entry;
  };

  const statuses = traits.map((t) => (scoring?.[`${t}_status`] as number) ?? 0);
  const total = traits.length;

  const count2 = statuses.filter((s) => s === 2).length;
  const count1 = statuses.filter((s) => s === 1).length;
  const count0 = statuses.filter((s) => s === 0).length;

  const traits2 = traits.filter((_, i) => statuses[i] === 2);
  const traits1 = traits.filter((_, i) => statuses[i] === 1);
  const traits0 = traits.filter((_, i) => statuses[i] === 0);

  const highText = buildSummaryText(traits2, lookup);
  const midText = buildSummaryText(traits1, lookup);
  const lowText = buildSummaryText(traits0, lookup);
  const lowLessText = buildSummaryText(traits0, lookup, "summaryLess");

  const proHigh = buildProText(traits2, lookup);
  const proMid = buildProText(traits1, lookup);
  const conLow = buildConText(traits0, lookup);

  const sentence1 = buildSentence1(count0, count1, count2, total, txt);
  const sentence2 = buildSentence2(count0, count1, count2, highText, midText, lowText, lowLessText, txt);
  const strengths = buildSentence3(count0, count1, count2, proMid, proHigh, txt);
  const tradeoffs = buildSentence4(count0, count1, count2, conLow, txt);

  return {
    // Joined with a single space so literal authors don't need to preserve the trailing space on Sentence1 values
    summary: [sentence1.trim(), sentence2.trim()].filter(Boolean).join(" "),
    strengths: strengths.trim(),
    tradeoffs: tradeoffs.trim(),
  };
}

/**
 * Returns the definition and behaviours for a trait (used in the Section 4 expandable rows).
 */
export function getTraitContent(traitKey: Trait, i18n?: VueI18n): { definition: string; behaviours: string[] } {
  const entry = resolveContentEntry(traitKey, i18n);
  if (!entry) return { definition: "", behaviours: [] };
  return { definition: entry.description, behaviours: entry.behaviours };
}

/**
 * Returns developmental suggestions for a trait (used in Section 6 for less-prioritized traits).
 */
export function getTraitDevSuggestions(traitKey: Trait, i18n?: VueI18n): string[] {
  return resolveContentEntry(traitKey, i18n)?.devSuggestions ?? [];
}
